import { PlatformEnum } from '@fluenceur/types';
import type { ComponentType } from 'react';
import type { IntegrationCardVariant } from './IntegrationCard';
import { ChatGptIntegration, ClaudeIntegration } from './assistants/AssistantIntegration';
import GmailIntegration from './GmailIntegration';
import GoogleDriveIntegration from './GoogleDriveIntegration';
import InstagramIntegration from './InstagramIntegration';
import MetaAdsIntegration from './MetaAdsIntegration';
import OutlookIntegration from './OutlookIntegration';
import ShopifyIntegration from './ShopifyIntegration';
import TikTokIntegration from './TikTokIntegration';

/** Connection data + presentation props every integration component accepts. */
export interface IntegrationComponentProps {
  id: string | null;
  isConnected: boolean;
  needsRefresh?: boolean;
  senderEmail?: string;
  metadata?: unknown;
  variant?: IntegrationCardVariant;
  detailHref?: string;
}

export type IntegrationCategoryKey =
  | 'assistants'
  | 'socialMedia'
  | 'advertising'
  | 'eCommerce'
  | 'email'
  | 'storage';

export interface IntegrationCatalogEntry {
  /** Matches both the route param and the backend `platform` field. */
  platform: string;
  category: IntegrationCategoryKey;
  Component: ComponentType<IntegrationComponentProps>;
}

/** Category render order on the list page. */
export const INTEGRATION_CATEGORY_ORDER: IntegrationCategoryKey[] = [
  'assistants',
  'socialMedia',
  'advertising',
  'eCommerce',
  'email',
  'storage',
];

/**
 * Single source of truth: list rows, the detail sub-page and the type filter
 * all read this. Components carry platform-specific `metadata` shapes, so each
 * is cast to the shared props contract at registration (the page used to do the
 * same cast inline).
 */
const asEntry = (Component: ComponentType<never>): ComponentType<IntegrationComponentProps> =>
  Component as unknown as ComponentType<IntegrationComponentProps>;

export const INTEGRATION_CATALOG: IntegrationCatalogEntry[] = [
  { platform: 'claude', category: 'assistants', Component: ClaudeIntegration },
  { platform: 'chatgpt', category: 'assistants', Component: ChatGptIntegration },
  { platform: 'instagram', category: 'socialMedia', Component: asEntry(InstagramIntegration) },
  { platform: 'tiktok', category: 'socialMedia', Component: asEntry(TikTokIntegration) },
  {
    platform: PlatformEnum.MetaAds,
    category: 'advertising',
    Component: asEntry(MetaAdsIntegration),
  },
  { platform: 'shopify', category: 'eCommerce', Component: asEntry(ShopifyIntegration) },
  { platform: 'gmail', category: 'email', Component: asEntry(GmailIntegration) },
  { platform: 'outlook', category: 'email', Component: asEntry(OutlookIntegration) },
  {
    platform: PlatformEnum.GoogleDrive,
    category: 'storage',
    Component: asEntry(GoogleDriveIntegration),
  },
];

export const findIntegrationEntry = (platform?: string): IntegrationCatalogEntry | undefined =>
  INTEGRATION_CATALOG.find((entry) => entry.platform === platform);

export const detailHrefFor = (platform: string): string => `/settings/integrations/${platform}`;

/** A single integration row from `useIntegrationsQuery`. */
export interface FetchedIntegration {
  platform: string;
  id?: string;
  isConnected?: boolean;
  needsRefresh?: boolean;
  senderEmail?: string;
  metadata?: unknown;
}

/** Connection props passed to an integration component, with safe defaults. */
export interface IntegrationConnection {
  id: string | null;
  isConnected: boolean;
  needsRefresh: boolean;
  senderEmail?: string;
  metadata?: unknown;
}

/**
 * Normalizes a fetched integration (or `undefined`) into the connection props
 * every integration component expects. Shared by the list and the detail page
 * so the defaulting lives in one place.
 */
export const toIntegrationConnection = (fetched?: FetchedIntegration): IntegrationConnection => ({
  id: fetched?.id ?? null,
  isConnected: fetched?.isConnected ?? false,
  needsRefresh: fetched?.needsRefresh ?? false,
  senderEmail: fetched?.senderEmail,
  metadata: fetched?.metadata,
});
